import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { threatsApi } from '../../api/threats'
import { weeklyApi } from '../../api/weekly'
import { RefusedError } from '../../api/client'
import { ThreatsPanel } from '../../components/ThreatsPanel'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import { citationByKey, exportText, shiftDays, weekLabel } from '../../lib/weekly'
import type { MovementSlice } from '../../types/weekly'
import { FunnelBand } from './FunnelBand'
import { Glance } from './Glance'
import { HistoryPanel } from './HistoryPanel'
import { MovementCard } from './MovementCard'
import { MovementRail } from './MovementRail'
import { Narrative } from './Narrative'
import { WeekAhead } from './WeekAhead'
import './weekly.css'

// The weekly update (REQ-024, Don's CS update): the week at a glance, then each slice of movement with the
// evidence behind it, the written narrative with every sentence tracing to a fact on file, the week ahead, and
// the history. Everything here is computed by the API against the last written update; the prose is a model's
// reading of those facts, written as a job, stored per week.

export type RailPick = 'all' | MovementSlice

export function Weekly() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const week = params.get('week') ?? undefined
  const [pick, setPick] = useState<RailPick>('all')
  const [copied, setCopied] = useState(false)

  const q = useQuery({
    queryKey: ['weekly', spoke, week ?? 'now'],
    queryFn: () => weeklyApi.view(spoke, week),
    staleTime: 15_000,
    refetchInterval: (query) => (query.state.data?.activeJobId ? 3000 : false),
  })
  const write = useMutation({
    mutationFn: () => weeklyApi.write(spoke, q.data?.movement.week.start),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['weekly'] }),
  })
  // the threats board, filtered to this week: a rival at one of our accounts leads the update
  const threats = useQuery({ queryKey: ['threats', spoke], queryFn: () => threatsApi.list(spoke), staleTime: 30_000 })
  useEffect(() => { setPick('all') }, [week])
  // when a write lands (the active job clears), the narrative and the history both changed on the server
  const active = q.data?.activeJobId ?? null
  const wasActive = useRef<string | null>(null)
  useEffect(() => {
    if (wasActive.current && !active) { void qc.invalidateQueries({ queryKey: ['weekly'] }); void qc.invalidateQueries({ queryKey: ['weekly-history', spoke] }) }
    wasActive.current = active
  }, [active, qc, spoke])
  useEffect(() => {
    if (!copied) return
    const id = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(id)
  }, [copied])

  const v = q.data
  const cites = useMemo(() => citationByKey(v?.citations ?? []), [v])
  const goTo = (day: string) => setParams({ week: day })
  const copy = async () => { if (!v) return; await navigator.clipboard.writeText(exportText(v)); setCopied(true) }
  const download = () => {
    if (!v) return
    const blob = new Blob([exportText(v)], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `weekly-update-${v.movement.week.start}.txt`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section className="page weekly">
      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p></div>}

      {v && (() => {
        const m = v.movement
        const mf = m.measuredFrom
        const inFuture = m.week.start > new Date().toISOString().slice(0, 10)
        const items = pick === 'all' ? m.items : m.items.filter((i) => i.slice === pick)
        const grouped = pick === 'all' ? m.slices.filter((s) => s.count > 0).map((s) => [s.title, items.filter((i) => i.slice === s.slice)] as const) : [[m.slices.find((s) => s.slice === pick)?.title ?? '', items] as const]
        return (
          <>
            <header className="weekly-head">
              <div>
                <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · Weekly update · week of {weekLabel(m.week)}{scopeWords(v.scope) ? ` · ${scopeWords(v.scope)}` : ''}</p>
                <h1>Weekly update</h1>
                <p className="lede">
                  Measured from {mf.threadsActive.toLocaleString()} thread{mf.threadsActive === 1 ? '' : 's'} active this week, {mf.meetingsHeld} external meeting{mf.meetingsHeld === 1 ? '' : 's'} held, and {mf.signalsRaised} signal{mf.signalsRaised === 1 ? '' : 's'} raised
                  {mf.hasBaseline
                    ? `, against the pipeline as it stood when the update of ${mf.baselineTakenOn ? formatDate(mf.baselineTakenOn) : 'the week before'} was written.`
                    : '. No earlier update exists to measure against, so stage moves wait for the first one; the dated facts carry this week.'}
                  {mf.fromStoredWeek ? ' This week reads as it was written, not as the pipeline stands today.' : ''}
                </p>
              </div>
              <div className="actions weekly-actions">
                <span className="week-nav">
                  <button className="button" onClick={() => goTo(shiftDays(m.week.start, -7))} title="The week before">Previous</button>
                  {!v.isCurrentWeek && <button className="button" onClick={() => setParams({})}>This week</button>}
                  <button className="button" onClick={() => goTo(shiftDays(m.week.start, 7))} disabled={v.isCurrentWeek} title="The week after">Next</button>
                </span>
                {v.activeJobId
                  ? <Link className="button primary" to={`/jobs/${encodeURIComponent(v.activeJobId)}`}>Watch the write</Link>
                  : <button className="button primary" onClick={() => write.mutate()} disabled={write.isPending || inFuture} title={v.modelConfigured ? 'Reads the facts on this page and writes the update, every sentence citing the fact it rests on. Runs as a job.' : 'No model is connected: the facts are laid out in plain sentences and marked as such.'}>
                      {write.isPending ? 'Starting…' : v.narrative ? 'Rewrite this week' : 'Write this week'}
                    </button>}
                <button className="button" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy as text'}</button>
                <button className="button" onClick={download}>Export .txt</button>
              </div>
            </header>
            {write.isError && <p className="error">{write.error instanceof RefusedError ? write.error.refusal.message : write.error.message}</p>}

            {v.isCurrentWeek && <FunnelBand />}
            <Glance view={v} onPick={setPick} />

            {(() => {
              const thisWeek = (threats.data?.threats ?? []).filter((t) => t.occurredOn >= m.week.start && t.occurredOn <= m.week.end)
              return (
                <ThreatsPanel
                  threats={thisWeek}
                  title="Threats this week"
                  emptyNote={threats.isPending ? 'Reading the threats board…' : 'No rival was seen at any of your accounts this week.'}
                />
              )
            })()}

            <div className="weekly-body">
              <MovementRail slices={m.slices} total={m.items.length} current={pick} onPick={setPick} />
              <div className="weekly-detail">
                {m.items.length === 0 && (
                  <div className="card">
                    <p className="muted">Nothing moved this week on file: no thread active, no external meeting, no signal raised, no tactic applied. Read new mail or sync the calendar and this fills in.</p>
                  </div>
                )}
                {grouped.map(([title, list]) => list.length > 0 && (
                  <section key={title} className="movement-group">
                    {(() => { const s = m.slices.find((x) => x.title === title); const more = s && s.count > s.shown ? s.count - s.shown : 0; return (
                      <h2 className="movement-group-title">{title}<span className="muted small">{more > 0 ? `the ${s!.shown} strongest of ${s!.count.toLocaleString()}` : list.length}</span></h2>
                    ) })()}
                    <div className="movement-cards">
                      {list.map((i) => <MovementCard key={i.key} item={i} cited={v.narrative?.citations.some((c) => c.key === i.key) ?? false} />)}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            <Narrative view={v} cites={cites} onWrite={() => write.mutate()} writing={write.isPending || !!v.activeJobId} />
            <WeekAhead ahead={m.ahead} calendarConnected={v.calendarConnected} />
            <HistoryPanel currentWeek={m.week.start} onPick={goTo} />
          </>
        )
      })()}
    </section>
  )
}
